'use client'
import type { PartialMessage } from '@bufbuild/protobuf'
import type { GetPagePublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/page/v1/page_pb'
import type { GetStoreProductPublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/store_product/v1/store_product_pb'
import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'

import NotFound from '@/app/not-found'
import { PageFooter } from '@/components/page-footer/page-footer'
import { FooterCta } from '@/components/page-footer/shared/footer-cta'
import { PageBackground } from '@/components/public-page/page-background'
import { PageFont } from '@/components/public-page/page-font'
import { PageNavBar } from '@/components/public-page/page-navbar'
import { PageStyle } from '@/components/public-page/page-style'
import { PageView } from '@/components/public-page/page-view'
import { ScaleFactor } from '@/components/public-page/scale-factor'
import { PageMeta } from '@/components/shared/page-meta'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { PHProvider } from '@/lib/posthog/providers'

import { ViewCartButton } from '../cart/view-cart-button'
import { PageWhiteShadow } from '../public-page/page-white-shadow'
import { PublicProductInner } from './public-product-inner'

type Props = {
  siteSlug: string
  productSlug: string
  data: PartialMessage<GetPagePublicResponse>
  productData: PartialMessage<GetStoreProductPublicResponse>
  showNavBar?: boolean
  hasBackground?: boolean
  domain?: string
}

export const PublicProductPage = ({
  siteSlug,
  productSlug,
  data,
  productData,
  showNavBar = true,
  hasBackground = true,
  domain,
}: Props) => {
  const { t } = useTranslation()
  const product = productData?.product

  if (!product) {
    return <NotFound />
  }

  return (
    <>
      {data?.site?.customHtmlBodyStart && (
        <div
          dangerouslySetInnerHTML={{ __html: data.site.customHtmlBodyStart }}
        />
      )}

      {hasBackground && <PageBackground content={data?.pageTheme?.content} />}

      <div className="relative flex h-full flex-col">
        <PageStyle content={data?.pageTheme?.content} noScaleFactor />
        <PageFont content={data?.pageTheme?.content} />
        <ScaleFactor />

        <div className="relative z-1 w-full flex-1 font-page-body font-page-body-weight text-page-text">
          <div className="relative z-30 mx-auto w-full max-w-[calc(480px*var(--scale-factor))] lg:my-fluid-4">
            <div className="p-fluid-4 pb-0">
              <div className="ft-widget-wrapper size-full rounded-page-widget text-neutral-900">
                <div className="bg-page-widget-background shadow-page-widget border-page-widget size-full rounded-page-widget text-left text-page-body-text backdrop-blur-page-widget">
                  <div className="px-block pt-fluid-6">
                    <div className="mb-fluid-4">
                      <Breadcrumb>
                        <BreadcrumbList>
                          <BreadcrumbItem>
                            <BreadcrumbLink
                              href={domain ? '/' : `/${siteSlug}`}
                              className="text-page-body-text hover:text-page-body-text"
                            >
                              {data?.site?.name || siteSlug}
                            </BreadcrumbLink>
                          </BreadcrumbItem>

                          <BreadcrumbSeparator className="text-page-body-text" />

                          <BreadcrumbItem>
                            <BreadcrumbLink
                              href={`${domain ? '/' : `/${siteSlug}/`}products`}
                              className="text-page-body-text hover:text-page-body-text"
                            >
                              {t('nav_products')}
                            </BreadcrumbLink>
                          </BreadcrumbItem>

                          <BreadcrumbSeparator className="text-page-body-text" />

                          <BreadcrumbItem>
                            <BreadcrumbPage className="text-page-body-text">
                              {product.name}
                            </BreadcrumbPage>
                          </BreadcrumbItem>
                        </BreadcrumbList>
                      </Breadcrumb>
                    </div>
                  </div>

                  <div className="rounded-b-page-widget-inner bg-card px-fluid-4 py-fluid-6 text-foreground">
                    <PublicProductInner
                      siteSlug={siteSlug}
                      productSlug={productSlug}
                      productData={productData}
                      domain={domain}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-fluid-8 px-fluid-4">
              <PageFooter
                page={data?.page}
                footerSlug={data?.pageTheme?.content?.footerSlug}
                hideFingertipBranding={data.site?.hideFingertipBranding}
                siteSlug={siteSlug}
                domain={domain}
              >
                <div className="p-block flex justify-center pt-fluid-8!">
                  <FooterCta siteSlug={siteSlug} />
                </div>
              </PageFooter>
            </div>
          </div>

          <ViewCartButton siteSlug={siteSlug} domain={domain} />

          <PageWhiteShadow />
        </div>

        <PHProvider>
          {showNavBar && (
            <PageNavBar
              siteSlug={siteSlug}
              pageSlug="index"
              domain={domain}
              data={data}
            />
          )}
        </PHProvider>
      </div>

      <PHProvider>
        <Suspense fallback={null}>
          <PageView
            data={{
              page_slug: 'index',
              page_id: data.page?.id,
              site_slug: siteSlug,
              site_id: data.site?.id,
            }}
          />
        </Suspense>
      </PHProvider>

      <PageMeta site={data?.site} />
    </>
  )
}
